import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckIcon, FlagIcon, UndoIcon } from 'lucide-react';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { LIST_QUERY_KEYS, useUrlSearch } from '@/lib/use-list-query-state';

/**
 * Capability gaps — developer-only (the server re-checks the flag on every
 * request; this page simply 403s for everyone else). Top: the nightly
 * consolidated digests the dreaming pass produces, ranked by relevance.
 * Bottom: the raw flags as they came in, for spot-checking the consolidation.
 */

const GAP_KIND_LABELS: Readonly<Record<string, string>> = {
  tool: 'Missing tool',
  context: 'Unreachable context',
  feature: 'Missing feature',
};

interface DigestItem {
  readonly title: string;
  readonly kind: string;
  readonly relevance: number;
  readonly recurring: boolean;
  readonly rationale: string;
  readonly count: number;
  readonly status: 'open' | 'resolved';
}

interface Digest {
  readonly id: string;
  readonly rangeStart: string;
  readonly rangeEnd: string;
  readonly gapsReviewed: number;
  readonly summary: string;
  readonly items: readonly DigestItem[];
  readonly emailed: boolean;
  readonly createdAt: string;
}

interface RawGap {
  readonly id: string;
  readonly question: string;
  readonly missing: string;
  readonly kind: string;
  readonly source: string;
  readonly createdAt: string;
}

/** The two lists search independently, so each keeps its own URL key. */
const GAP_SEARCH_KEYS = { digests: LIST_QUERY_KEYS.search, flags: 'flagsQ' } as const;

/** A digest item with its position (the resolve API addresses items by index) and kind label. */
interface DigestRow extends DigestItem {
  readonly index: number;
  readonly kindLabel: string;
}

/** A raw flag with its kind label, for the search. */
interface RawGapRow extends RawGap {
  readonly kindLabel: string;
}

/** The fields each search box matches. */
const DIGEST_SEARCH_FIELDS: ReadonlyArray<keyof DigestRow> = ['title', 'kindLabel', 'rationale'];
const FLAG_SEARCH_FIELDS: ReadonlyArray<keyof RawGapRow> = ['question', 'missing', 'kindLabel', 'source'];

function kindLabel(kind: string): string {
  return GAP_KIND_LABELS[kind] ?? kind;
}

function relevanceTone(relevance: number): 'danger' | 'success' | 'neutral' {
  if (relevance >= 4) return 'danger';
  if (relevance === 3) return 'success';
  return 'neutral';
}

export function GapsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const setStatus = useMutation({
    mutationFn: (input: { digestId: string; itemIndex: number; status: 'open' | 'resolved' }) =>
      sendJson('patch', `/assistant/gaps/digests/${input.digestId}/items`, {
        itemIndex: input.itemIndex,
        status: input.status,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assistant-gap-digests'] });
    },
  });
  const digests = useQuery({
    queryKey: ['assistant-gap-digests'],
    queryFn: ({ signal }) => getJson<Digest[]>('/assistant/gaps/digests', undefined, signal),
  });
  const raw = useQuery({
    queryKey: ['assistant-gaps-raw'],
    queryFn: ({ signal }) => getJson<RawGap[]>('/assistant/gaps', undefined, signal),
  });
  const digestSearch = useUrlSearch(GAP_SEARCH_KEYS.digests);
  const flagSearch = useUrlSearch(GAP_SEARCH_KEYS.flags);

  const digestRows = (digests.data ?? [])
    .map((digest) => ({
      digest,
      items: filterRows(
        digest.items.map((item, index) => ({ ...item, index, kindLabel: kindLabel(item.kind) })),
        digestSearch.search,
        DIGEST_SEARCH_FIELDS,
      ).sort((a, b) => b.relevance - a.relevance),
    }))
    .filter(({ items }) => items.length > 0);
  const flagRows = filterRows(
    (raw.data ?? []).map((gap) => ({ ...gap, kindLabel: kindLabel(gap.kind) })),
    flagSearch.search,
    FLAG_SEARCH_FIELDS,
  );

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="AI ASSISTANT"
        title="Capability gaps"
        subtitle="Legitimate questions the assistant couldn't answer — consolidated nightly, rated by relevance, emailed each morning."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search gaps"
          placeholder="Gap, kind or why"
          value={digestSearch.search}
          onChange={(event) => digestSearch.setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
      </div>

      {digests.isPending ? (
        <SkeletonTable label="Loading digests…" />
      ) : digests.isError ? (
        <ErrorNotice title="Digests unavailable" message={errorMessage(digests.error)} />
      ) : digests.data.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<FlagIcon aria-hidden className="size-6" />}
            title="No digests yet"
            message="When the assistant flags questions it couldn't answer, the nightly dreaming pass consolidates them here (and into the morning email)."
          />
        </Panel>
      ) : digestRows.length === 0 ? (
        <Panel>
          <EmptyState title="No gaps match" message="Try a different search." />
        </Panel>
      ) : (
        digestRows.map(({ digest, items }) => (
          <Panel key={digest.id} className="mb-4">
            <div className="px-5 pt-5 pb-4">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <h3 className="text-portal-card-title font-bold text-portal-ink">
                  {new Date(digest.createdAt).toLocaleDateString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                </h3>
                <p className="text-portal-micro text-portal-muted">
                  {digest.gapsReviewed} {digest.gapsReviewed === 1 ? 'flag' : 'flags'} reviewed
                  {digest.emailed ? ' · emailed' : ''}
                </p>
              </div>
              {digest.summary ? (
                <p className="mt-1 text-portal-micro text-portal-muted">{digest.summary}</p>
              ) : null}
            </div>
            <DataTable headers={['Gap', 'Kind', 'Relevance', 'Flags', 'Why', 'Status']} minWidth={860} flush>
              {items.map((item) => (
                <tr
                  key={item.index}
                  className={`border-t border-portal-line align-top ${item.status === 'resolved' ? 'opacity-55' : ''}`}
                >
                  <td className="p-3 text-portal-small font-semibold text-portal-ink">
                    {item.recurring ? (
                      <span title="Recurring — appeared in earlier digests">↻ </span>
                    ) : null}
                    {item.title}
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <Pill>{item.kindLabel}</Pill>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <Pill tone={relevanceTone(item.relevance)}>{item.relevance}/5</Pill>
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted">{item.count}</td>
                  <td className="p-3 text-portal-small text-portal-muted">{item.rationale}</td>
                  <td className="p-3 whitespace-nowrap">
                    {item.status === 'resolved' ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Pill tone="success">Resolved</Pill>
                        <button
                          type="button"
                          aria-label="Reopen this gap"
                          disabled={setStatus.isPending}
                          onClick={() =>
                            setStatus.mutate({ digestId: digest.id, itemIndex: item.index, status: 'open' })
                          }
                          className="cursor-pointer rounded-portal-button p-1 text-portal-muted hover:bg-admin-subtle hover:text-portal-ink"
                        >
                          <UndoIcon aria-hidden className="size-3.5" />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={setStatus.isPending}
                        onClick={() =>
                          setStatus.mutate({ digestId: digest.id, itemIndex: item.index, status: 'resolved' })
                        }
                        className="inline-flex cursor-pointer items-center gap-1 rounded-portal-button border border-portal-line px-2 py-1 text-portal-micro font-bold text-portal-muted hover:border-portal-success hover:text-portal-success"
                      >
                        <CheckIcon aria-hidden className="size-3.5" /> Mark resolved
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </DataTable>
          </Panel>
        ))
      )}

      <h2 className="mb-2 mt-8 text-portal-section font-bold text-portal-ink">Raw flags</h2>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search raw flags"
          placeholder="Question, what was missing, kind or source"
          value={flagSearch.search}
          onChange={(event) => flagSearch.setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
      </div>
      {raw.isPending ? (
        <SkeletonTable label="Loading flags…" />
      ) : raw.isError ? (
        <ErrorNotice title="Flags unavailable" message={errorMessage(raw.error)} />
      ) : raw.data.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<FlagIcon aria-hidden className="size-6" />}
            title="No raw flags"
            message="Nothing has been flagged yet — the assistant files a flag whenever a real BCE question outruns its tools."
          />
        </Panel>
      ) : flagRows.length === 0 ? (
        <Panel>
          <EmptyState title="No flags match" message="Try a different search." />
        </Panel>
      ) : (
        <DataTable headers={['When', 'Question', 'What was missing', 'Kind', 'Source']} minWidth={760}>
          {flagRows.map((gap) => (
            <tr key={gap.id} className="border-t border-portal-line align-top">
              <td className="p-3 whitespace-nowrap text-portal-small text-portal-muted">
                {new Date(gap.createdAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </td>
              <td className="p-3 text-portal-small text-portal-ink">{gap.question}</td>
              <td className="p-3 text-portal-small text-portal-muted">{gap.missing}</td>
              <td className="p-3 whitespace-nowrap">
                <Pill>{gap.kindLabel}</Pill>
              </td>
              <td className="p-3 text-portal-small text-portal-muted">{gap.source}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </section>
  );
}
